import { t } from "i18next";
import {
  Alert,
  Heading,
  IconWarningSolid,
  List,
  Text,
  ToggleDetails,
} from "@instructure/ui";
import { useEffect, useState } from "react";
import DataGrid from "react-data-grid";
import Skeleton from "react-loading-skeleton";
import { useLocation, useOutletContext } from "react-router";
import {
  buildOutcomeStatementMap,
  getOutcomeStaleness,
  sanitizeStatement,
} from "../../../../../components/LearningOutcomes/utils";
import { Selector } from "../../../../../components/Selectors/Selector";
import { AssignmentService } from "../../../../../services/AssignmentService";
import { buildAssignmentTable } from "../utils";

const CanvasAssignment = () => {
  const { setNextEnabled, assignments, setFormData, formData } =
    useOutletContext();
  const [selectedAssignmentId, setSelectedAssignmentId] = useState(
    formData.metadata?.assignmentId || ""
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [staleOutcomes, setStaleOutcomes] = useState([]);
  const location = useLocation();
  const section = location.state.section;

  useEffect(() => {
    if (formData.metadata.results && "assignmentId" in formData.metadata) {
      const {
        outcomeColumns,
        studentRows,
        staleOutcomes: stale,
      } = buildAssignmentTable(formData.metadata.results);
      setColumns(outcomeColumns);
      setRows(studentRows);
      setStaleOutcomes(stale);
    }
  }, [formData.metadata.results]); // eslint-disable-line

  async function handleSelectAssignment(assignmentId) {
    try {
      setLoading(true);
      setSelectedAssignmentId(assignmentId);
      const results = await AssignmentService.getResultsByContext(
        AssignmentService.ASSIGNMENT,
        assignmentId
      );

      const statementMap = buildOutcomeStatementMap(section.learning_outcomes);

      const approved_lo_results = results["data"]
        .filter(
          (result) =>
            getOutcomeStaleness(
              sanitizeStatement(result["outcome_description"]),
              statementMap
            ) !== null
        )
        .map((result) => ({
          ...result,
          _staleness: getOutcomeStaleness(
            sanitizeStatement(result["outcome_description"]),
            statementMap
          ),
        }));
      results["data"] = approved_lo_results;
      setNextEnabled(true);
      setFormData((prev) => ({
        ...prev,
        metadata: {
          assignmentId: assignmentId,
          assignmentName: assignments.find((a) => a.id === assignmentId).name,
          results: results,
        },
      }));
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "0.75rem",
        height: "100%",
      }}
    >
      <Heading border="bottom" level="h3" as="h2" width="100%">
        {t("finalizeCourse.canvasAssignment")}
      </Heading>
      <Selector
        id="assignment-selector"
        label={t("assignment.assignment")}
        options={assignments}
        selectHandler={handleSelectAssignment}
        selectedItemId={selectedAssignmentId}
      />
      {loading ? (
        <div style={{ flex: "1" }}>
          <Skeleton height="100%" />
        </div>
      ) : rows.length > 0 && columns.length > 1 ? (
        <>
          {staleOutcomes.length > 0 && (
            <ToggleDetails
              size="small"
              summary={
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "0.25rem",
                  }}
                >
                  <IconWarningSolid color="warning" size="x-small" />
                  {t("finalizeCourse.pastOutcomeResults")}
                </span>
              }
            >
              <Text as="p" size="small">
                {t("finalizeCourse.pastOutcomeVersionDisclaimer")}
              </Text>
              <br />
              <List isUnstyled>
                {staleOutcomes.map(({ name, description }) => (
                  <List.Item key={name}>
                    <Text size="small">
                      <Text size="small" weight="bold">
                        {name}:{" "}
                      </Text>
                      {description}
                    </Text>
                  </List.Item>
                ))}
              </List>
            </ToggleDetails>
          )}
          <DataGrid
            className="rdg-light"
            style={{ flex: "1", marginBottom: "0.05rem" }}
            rowHeight={60}
            headerRowHeight={40}
            columns={columns}
            rows={rows}
          />
        </>
      ) : error ? (
        <Alert variant="error">
          {t("notifications.general.fetchError", {
            obj: t("finalizeCourse.assignmentResults"),
          })}
        </Alert>
      ) : selectedAssignmentId ? (
        <Alert>{t("finalizeCourse.noAssignmentResults")}</Alert>
      ) : (
        <Alert>{t("finalizeCourse.selectAnAssignment")}</Alert>
      )}
    </div>
  );
};

export default CanvasAssignment;
